import {
  Briefcase,
  ClipboardList,
  FileText,
  Lightbulb,
  Pencil,
  Scale,
  Type,
  type LucideIcon
} from "lucide-react";

import type {
  CasePrepUscisFieldProvenance,
  CasePrepUscisSourceType
} from "../../types";

export type FieldSourceKind =
  | "document"
  | "intake"
  | "key_fact"
  | "attorney_profile"
  | "pasted"
  | "filing_rule"
  /** Typed in by a person, with no source behind it. */
  | "manual";

type SourceKindSpec = {
  label: string;
  icon: LucideIcon;
  /** Text color class for the icon only; tags and rows stay neutral. */
  hue: string;
  /** The same hue as a fill, for the page stripes and the legend. */
  stripe: string;
  /** The hover card's first action. */
  openLabel: string;
};

/** Icon, hue and label per kind, shared with the page stripes and their legend. */
export const SOURCE_KINDS: Record<FieldSourceKind, SourceKindSpec> = {
  document: {
    label: "Document",
    icon: FileText,
    hue: "text-info",
    stripe: "bg-info",
    openLabel: "Open Source"
  },
  intake: {
    label: "Intake",
    icon: ClipboardList,
    hue: "text-purple",
    stripe: "bg-purple",
    openLabel: "Open Intake"
  },
  key_fact: {
    label: "Case Insights",
    icon: Lightbulb,
    hue: "text-orange",
    stripe: "bg-orange",
    openLabel: "Open Source"
  },
  attorney_profile: {
    label: "Attorney Profile",
    icon: Briefcase,
    // The palette has no cyan; the intake kind's teal is the nearest token.
    hue: "text-kind-intake",
    stripe: "bg-kind-intake",
    openLabel: "Open Profile"
  },
  pasted: {
    label: "Pasted Text",
    icon: Type,
    hue: "text-fg-3",
    stripe: "bg-fg-3",
    openLabel: "Open Source"
  },
  filing_rule: {
    label: "Filing Rule",
    icon: Scale,
    hue: "text-fg-3",
    stripe: "bg-fg-3",
    openLabel: "Open Filing Rules"
  },
  manual: {
    label: "Manual Edit",
    icon: Pencil,
    hue: "text-warn",
    stripe: "bg-warn",
    openLabel: "Open Source"
  }
};

/** The kind a fill step's source type stands for (the step tags in the source list). */
export function sourceTypeKind(
  sourceType: CasePrepUscisSourceType | string | null | undefined
): FieldSourceKind {
  return sourceKindOf({ source_type: sourceType ?? null });
}

/** A legend entry on the page: one or more kinds under one stripe color. */
export type SourceLegendKey =
  | "document"
  | "intake"
  | "attorney_profile"
  | "key_fact"
  | "other"
  | "edited";

type SourceLegendSpec = { key: SourceLegendKey; label: string; stripe: string };

/** In legend order. Pasted text and filing rules share Other; every edit is Edited. */
export const SOURCE_LEGEND: SourceLegendSpec[] = [
  { key: "document", label: "Document", stripe: SOURCE_KINDS.document.stripe },
  { key: "intake", label: "Intake", stripe: SOURCE_KINDS.intake.stripe },
  {
    key: "attorney_profile",
    label: "Profile",
    stripe: SOURCE_KINDS.attorney_profile.stripe
  },
  {
    key: "key_fact",
    label: "Case Insights",
    stripe: SOURCE_KINDS.key_fact.stripe
  },
  { key: "other", label: "Other", stripe: SOURCE_KINDS.pasted.stripe },
  { key: "edited", label: "Edited", stripe: SOURCE_KINDS.manual.stripe }
];

export const SOURCE_LEGEND_BY_KEY = Object.fromEntries(
  SOURCE_LEGEND.map((spec) => [spec.key, spec])
) as Record<SourceLegendKey, SourceLegendSpec>;

/**
 * The legend entry a field's stripe belongs to. An edit stands out as Edited
 * whatever its source was, so overrides show on a scan of the page.
 */
export function sourceLegendKeyOf(
  provenance: CasePrepUscisFieldProvenance | null | undefined
): SourceLegendKey | null {
  if (!provenance) return null;
  if (provenance.kind === "manual_edit") return "edited";
  const kind = sourceKindOf(provenance);
  if (kind === "pasted" || kind === "filing_rule") return "other";
  if (kind === "manual") return "edited";
  return kind;
}

/** The kind of one provenance entry, ignoring whether it was later edited. */
export function sourceKindOf(
  provenance: CasePrepUscisFieldProvenance | null | undefined
): FieldSourceKind {
  const kind = String(provenance?.kind || "");
  const sourceType = String(provenance?.source_type || "");
  if (kind === "manual_edit") return "manual";
  if (kind === "filing_rule" || sourceType === "filing_rule")
    return "filing_rule";
  if (kind === "key_fact" || sourceType === "key_fact") return "key_fact";
  if (kind === "attorney_profile" || sourceType === "attorney_profile")
    return "attorney_profile";
  if (sourceType === "intake") return "intake";
  if (sourceType === "free_text") return "pasted";
  return "document";
}

/**
 * The provenance entry that names where the value came from: the entry itself,
 * or for a manual edit the source it replaced. Null when nothing but a person
 * stands behind the value.
 */
export function sourceEntryOf(
  provenance: CasePrepUscisFieldProvenance | null | undefined
): CasePrepUscisFieldProvenance | null {
  if (!provenance) return null;
  const entry =
    provenance.kind === "manual_edit"
      ? provenance.previous ?? null
      : provenance;
  if (!entry || entry.kind === "manual_edit") return null;
  return entry;
}

/** The kind a field's card shows: an edited field keeps its source's kind. */
export function fieldSourceKindOf(
  provenance: CasePrepUscisFieldProvenance | null | undefined
): FieldSourceKind {
  return sourceKindOf(sourceEntryOf(provenance) ?? provenance);
}
